import { useEffect } from "react";
import { Loader2, Check, XCircle } from "lucide-react";
import { useLang } from "../lib/i18n.jsx";

// ─── BADGE ────────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  published:"bg-green", PUBLISHED:"bg-green",
  draft:"bg-gray",      DRAFT:"bg-gray",
  active:"bg-green",    ACTIVE:"bg-green",
  trial:"bg-orange",    TRIAL:"bg-orange",
  cancelled:"bg-red",   CANCELLED:"bg-red",
  expired:"bg-red",     EXPIRED:"bg-red",
  free:"bg-gray",       NONE:"bg-gray",
  success:"bg-green",   COMPLETED:"bg-green",
  failed:"bg-red",      pending:"bg-orange",
  PENDING:"bg-orange",  responded:"bg-green",
  SCHOLARSHIP:"bg-teal",ARCHIVED:"bg-gray",
};

const STATUS_I18N = {
  published:"status.published", PUBLISHED:"status.published",
  draft:"status.draft",         DRAFT:"status.draft",
  active:"status.active",       ACTIVE:"status.active",
  trial:"status.trial",         TRIAL:"status.trial",
  cancelled:"status.cancelled", CANCELLED:"status.cancelled",
  expired:"status.expired",     EXPIRED:"status.expired",
  free:"status.none",           NONE:"status.none",
  success:"status.completed",   COMPLETED:"status.completed",
  failed:"status.failed",       pending:"status.pending",
  PENDING:"status.pending",     responded:"status.responded",
  SCHOLARSHIP:"status.scholarship",
  archived:"status.archived",   ARCHIVED:"status.archived",
};

export function Badge({ status }) {
  const { t } = useLang();
  const i18nKey = STATUS_I18N[status];
  const label = i18nKey ? t(i18nKey) : status;
  return <span className={`badge ${STATUS_MAP[status] || "bg-gray"}`}>{label}</span>;
}

// ─── SPINNER ──────────────────────────────────────────────────────────────────
export function Spinner({ text = "Loading…" }) {
  return (
    <div className="loading-state">
      <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
      <span>{text}</span>
    </div>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────
export function Empty({ icon, message }) {
  return (
    <div className="empty-state">
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 13 }}>{message}</div>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
export function ProgBar({ pct }) {
  const cls = pct >= 70 ? "" : pct >= 40 ? "am" : "rs";
  return (
    <div className="prog-track">
      <div className={`prog-fill ${cls}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── SPARK LINE ───────────────────────────────────────────────────────────────
export function SparkBars({ color, hi }) {
  const vals = [55, 62, 58, 70, 75, 68, 80, 72, 85, 82, 90];
  return (
    <div className="spark">
      {vals.map((h, i) => (
        <div key={i} className="sp-bar"
          style={{ height: `${h}%`, background: i === vals.length - 1 ? hi : color }}
        />
      ))}
    </div>
  );
}

// ─── MODAL CLOSE BUTTON ───────────────────────────────────────────────────────
export function CloseX({ onClick }) {
  return <button className="modal-close" onClick={onClick}>✕</button>;
}

// ─── STAT CARD ────────────────────────────────────────────────────────────────
export function StatCard({ cls, label, value, sub, chg, chgType = "chg-up", sparkColor, sparkHi }) {
  return (
    <div className={`stat-card ${cls}`}>
      <div className="sc-lbl">{label}</div>
      <div className="sc-val">{value}</div>
      <div className="sc-sub">{sub}</div>
      {sparkColor && <SparkBars color={sparkColor} hi={sparkHi} />}
      <span className={`sc-chg ${chgType}`}>{chg}</span>
    </div>
  );
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`toast${toast.type === "error" ? " error" : toast.type === "ok" ? " ok" : ""}`}>
      {toast.type === "error" ? <XCircle size={16} /> : <Check size={16} />}
      {toast.msg}
    </div>
  );
}

// ─── USER AVATAR ─────────────────────────────────────────────────────────────
export function Avatar({ name, size = 30 }) {
  return (
    <div className="av" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {name?.[0]?.toUpperCase() ?? "?"}
    </div>
  );
}

// ─── PAGINATION ──────────────────────────────────────────────────────────────
export function Pagination({ page, totalPages, total, onPrev, onNext }) {
  const { t } = useLang();
  return (
    <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 12, color: "var(--ghost)" }}>
        {t("ui.page", { n: page, total: totalPages })} · {total?.toLocaleString()} {t("filter.all").toLowerCase()}
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-out btn-sm" disabled={page <= 1} onClick={onPrev}>‹ {t("common.prev")}</button>
        <button className="btn btn-teal btn-sm" disabled={page >= totalPages} onClick={onNext}>{t("common.next")} ›</button>
      </div>
    </div>
  );
}

// ─── MODAL — ESC key + backdrop click to close ───────────────────────────────
export function Modal({ children, onClose, maxWidth = 480 }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="modal-ov open"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal" style={{ maxWidth }} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ─── CONFIRM MODAL — destructive action confirmation ─────────────────────────
export function ConfirmModal({ title, message, confirmLabel = "Confirm", danger = false, onConfirm, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="modal-ov open" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-ttl">{title} <CloseX onClick={onClose} /></div>
        <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.6, marginBottom: 20, padding: "12px 14px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
          {message}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button
            className="btn"
            style={{ flex: 1, justifyContent: "center", background: danger ? "var(--rose)" : "var(--teal)", color: "white" }}
            onClick={() => { onConfirm(); onClose(); }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
